import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { getApiBaseUrl } from "../config/api";
import RichTextEditor from "./shared/RichTextEditor";
import CaptionAssist from "./shared/CaptionAssist";

const API_URL = getApiBaseUrl();
const CATEGORIES = [
  "General",
  "Really Lesson",
  "COOL",
  "ReallyAvatar",
  "Coursera",
  "TechTree",
];

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 14,
  borderRadius: 12,
  border: "1px solid rgba(148, 163, 184, 0.9)",
  marginBottom: 10,
  outline: "none",
  backgroundColor: "rgba(248, 250, 252, 0.95)",
  color: "#0f172a",
  boxSizing: "border-box",
};

export default function EditArticleForm({ post, user, onSuccess }) {
  const safe = post ?? {};
  const [title, setTitle] = useState(safe.title ?? "");
  const [content, setContent] = useState(safe.content ?? safe.text ?? "");
  const [category, setCategory] = useState(safe.category ?? "General");
  const [country] = useState(safe.country ?? user?.country ?? "");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(safe.cover_image || null);
  const [removeCover, setRemoveCover] = useState(false);
  const [loading, setLoading] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !file.type.startsWith("image/")) return;
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setCoverFile(file);
    setCoverPreview(url);
    setRemoveCover(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const plain = content.replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").trim();
    if (!title.trim() || !plain) {
      alert("Title and content are required.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("post_id", safe.id);
      formData.append("title", title.trim());
      formData.append("content", content);
      formData.append("category", category);
      formData.append("country", country);
      formData.append("post_type", "article");
      if (user?.id) formData.append("user_id", user.id);
      if (removeCover) formData.append("remove_cover", "1");

      if (coverFile) {
        try {
          const compressed = await imageCompression(coverFile, {
            maxSizeMB: 1.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
          formData.append("cover", compressed, compressed.name);
        } catch {
          formData.append("cover", coverFile);
        }
      }

      const { data } = await axios.post(`${API_URL}/update_post.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data.success) {
        onSuccess?.(data.post);
      } else {
        alert(data.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      alert("Update error: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>Edit article</h2>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Article headline"
        required
        style={inputStyle}
      />

      <div
        style={{
          border: "2px dashed #cbd5e1",
          borderRadius: 12,
          padding: 14,
          marginBottom: 12,
          textAlign: "center",
          background: "#f8fafc",
        }}
      >
        {coverPreview && !removeCover ? (
          <div>
            <img
              src={coverPreview}
              alt="Cover"
              style={{
                width: "100%",
                maxHeight: 180,
                objectFit: "cover",
                borderRadius: 8,
                marginBottom: 8,
              }}
            />
            <button
              type="button"
              onClick={() => {
                setRemoveCover(true);
                setCoverFile(null);
                setCoverPreview(null);
              }}
              style={{
                border: "none",
                background: "#fee2e2",
                color: "#b91c1c",
                padding: "4px 10px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 12,
                marginRight: 8,
              }}
            >
              Remove cover
            </button>
          </div>
        ) : (
          <span style={{ fontSize: 13, color: "#64748b" }}>No cover image</span>
        )}
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            onClick={() => document.getElementById("editArticleCover")?.click()}
            style={{
              border: "1px solid #cbd5e1",
              background: "#fff",
              padding: "6px 12px",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            {coverPreview && !removeCover ? "Change cover" : "Add cover"}
          </button>
          <input
            id="editArticleCover"
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleCoverChange}
          />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <RichTextEditor content={content} onChange={setContent} />
        <CaptionAssist
          kind="article"
          format="html"
          text={content}
          title={title}
          category={category}
          userId={user?.id || user?.user_id}
          disabled={loading}
          style={{ marginTop: 8 }}
          onApply={setContent}
          onApplyTitle={(nextTitle) => {
            if (!title.trim() && nextTitle) setTitle(nextTitle);
          }}
        />
      </div>

      <select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        style={{ ...inputStyle, backgroundColor: "#f8fafc" }}
      >
        {CATEGORIES.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>

      <button
        type="submit"
        disabled={loading}
        style={{
          background: "#0284c7",
          color: "#fff",
          border: "none",
          borderRadius: 999,
          padding: "11px 20px",
          width: "100%",
          cursor: loading ? "not-allowed" : "pointer",
          fontWeight: 600,
        }}
      >
        {loading ? "Saving..." : "Save article"}
      </button>
    </form>
  );
}
